import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TrendingUp, LayoutGrid, List as ListIcon, Play, Shuffle } from 'lucide-react';
import { AudioFile } from '../types';
import { audioService } from '../services/api';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import NaatCard from '../components/NaatCard';
import SEO from '../components/SEO';
import './PopularNaatsPage.css';

const PopularNaatsPage: React.FC = () => {
  const { page: pageParam } = useParams<{ page?: string }>();
  const navigate = useNavigate();
  
  const [popularAudios, setPopularAudios] = useState<AudioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [totalCount, setTotalCount] = useState(0);
  
  // Get page from URL path or default to 1
  const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
  const pageSize = 15; // Fixed page size for cleaner URLs
  
  const { state, playAudio, addToQueue, togglePlay, removeFromQueue } = useAudioPlayer();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();

  const MAX_NAATS = 100; // Restrict to maximum 100 popular naats
  const effectiveTotalCount = Math.min(totalCount, MAX_NAATS);
  const TOTAL_PAGES = Math.ceil(effectiveTotalCount / pageSize);

  // Use the current page data directly from API
  const currentPageData = popularAudios;

  useEffect(() => {
    const fetchPopularAudios = async () => {
      setLoading(true);
      try {
        // Restrict to first 100 popular naats by checking if we're within limit
        const maxPageForLimit = Math.ceil(MAX_NAATS / pageSize);
        if (currentPage > maxPageForLimit) {
          setPopularAudios([]);
          setTotalCount(0);
          return;
        }

        // Fetch paginated data for current page only
        const response = await audioService.getPopularPaginated(currentPage, pageSize);
        if (response.success && response.data) {
          setPopularAudios(response.data);
          // Ensure total count doesn't exceed our maximum
          setTotalCount(Math.min(response.total || 0, MAX_NAATS));
        } else {
          setPopularAudios([]);
          setTotalCount(0);
        }
      } catch (error) {
        setPopularAudios([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularAudios();
  }, [currentPage, pageSize]);

  const handlePlayAudio = (audio: AudioFile) => {
    // If this audio is already playing, just toggle play/pause
    if (state.currentAudio?.id === audio.id) {
      togglePlay();
    } else {
      playAudio(audio);
    }
  };

  const handleAddToQueue = (audio: AudioFile) => {
    const added = addToQueue(audio);
    if (added) {
      showToast(`Added "${audio.title}" to queue`, 'success', 3000, 'bottom-right');
    } else {
      showToast(`"${audio.title}" is already in queue`, 'info', 3000, 'bottom-right');
    }
  };

  const handleToggleFavorite = (audio: AudioFile) => {
    const wasAlreadyFavorite = isFavorite(audio.id);
    toggleFavorite(audio);
    if (wasAlreadyFavorite) {
      showToast(`Removed "${audio.title}" from favorites`, 'info', 3000, 'top-right-favorites');
    } else {
      showToast(`Added "${audio.title}" to favorites`, 'success', 3000, 'top-right-favorites');
    }
  };

  const handleDownloadComplete = (audio: AudioFile, success: boolean) => {
    if (success) {
      showToast(`Downloaded "${audio.title}" successfully! 🎵`, 'success', 4000, 'bottom-right');
    } else {
      showToast(`Failed to download "${audio.title}". Please try again.`, 'error', 4000, 'bottom-right');
    }
  };

  const handleLayoutChange = (mode: 'grid' | 'list') => {
    setLayout(mode);
  };

  const handlePageChange = (page: number) => {
    // Prevent navigation beyond maximum allowed pages
    const maxAllowedPage = Math.ceil(MAX_NAATS / pageSize);
    if (page > maxAllowedPage) {
      return;
    }

    // Navigate to SEO-friendly URL
    if (page === 1) {
      navigate('/popular', { replace: true });
    } else {
      navigate(`/popular/page/${page}`, { replace: true });
    }
    
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePlayAll = async () => {
    if (popularAudios && popularAudios.length > 0) {
      // Clear existing queue first
      state.queue.forEach(() => removeFromQueue(0));

      // For now, play current page naats.
      // In future, could fetch all popular naats if needed
      playAudio(popularAudios[0], popularAudios, 'regular');
      showToast(`Playing current page naats (${popularAudios.length} naats)`, 'success', 3000);
    } else {
      showToast('No naats available to play', 'error');
    }
  };

  const handleShuffle = async () => {
    if (popularAudios && popularAudios.length > 0) {
      // Clear existing queue first
      state.queue.forEach(() => removeFromQueue(0));

      // Create shuffled array from current page
      const shuffled = [...popularAudios].sort(() => 0.5 - Math.random());

      // Play shuffled playlist
      playAudio(shuffled[0], shuffled, 'regular');
      showToast(`Shuffling current page naats (${popularAudios.length} naats)`, 'success', 3000);
    } else {
      showToast('No naats available to shuffle', 'error');
    }
  };

  if (loading) {
    return (
      <div className="popular-naats-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Popular Naats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="popular-naats-page">
      <SEO
        title="Popular Naats - NaatCity"
        description="Listen to the most popular Islamic Naats on NaatCity. Discover trending naats from renowned Naat Khawans that are loved by millions of listeners worldwide."
        keywords="popular naats, trending islamic naats, most played naats, top naats, best naats collection"
        url="https://naatcity.com/popular"
      />
      <div className="page-header">
        <div className="header-content">
          <div className="header-center">
            <h1>
              <TrendingUp size={32} />
              Popular Naats
            </h1>
            <div className="results-count">
              {popularAudios.length > 0 ? (
                <>
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, effectiveTotalCount)} of {effectiveTotalCount} Popular Naats
                </>
              ) : (
                'No Popular Naats Found'
              )}
            </div>
            {popularAudios.length > 0 && (
              <div className="action-buttons">
                <button
                  className="btn btn-primary"
                  onClick={handlePlayAll}
                  aria-label="Play all popular naats"
                >
                  <Play size={16} />
                  Play All
                </button>
                <button
                  className="btn btn-outline"
                  onClick={handleShuffle}
                  aria-label="Shuffle and play all popular naats"
                >
                  <Shuffle size={16} />
                  Shuffle
                </button>
              </div>
            )}
          </div>
          <div className="layout-toggle" role="group" aria-label="Change layout">
            <button
              type="button"
              className={`layout-btn ${layout === 'grid' ? 'active' : ''}`}
              aria-pressed={layout === 'grid'}
              onClick={() => handleLayoutChange('grid')}
            >
              <LayoutGrid size={16} /> <span className="visually-hidden">Grid view</span>
            </button>
            <button
              type="button"
              className={`layout-btn ${layout === 'list' ? 'active' : ''}`}
              aria-pressed={layout === 'list'}
              onClick={() => handleLayoutChange('list')}
            >
              <ListIcon size={16} /> <span className="visually-hidden">List view</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-section">
        {(currentPageData || []).length === 0 && !loading ? (
          <div className="empty-state">
            <TrendingUp size={48} />
            <h3>No Popular Naats Found</h3>
            <p>There are currently no popular naats to display.</p>
          </div>
        ) : layout === 'list' ? (
          <div className="naat-card-list">
            {(currentPageData || []).map((audio, index) => {
              const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
              return (
                <NaatCard
                  key={audio.id}
                  naat={audio}
                  serialNumber={(currentPage - 1) * pageSize + index + 1}
                  isPlaying={isPlaying}
                  isFavorite={isFavorite(audio.id)}
                  onPlay={handlePlayAudio}
                  onPause={() => togglePlay()}
                  onAddToQueue={handleAddToQueue}
                  onToggleFavorite={handleToggleFavorite}
                  onDownloadComplete={handleDownloadComplete}
                />
              );
            })}
          </div>
        ) : (
          <div className="naat-card-grid">
            {(currentPageData || []).map((audio, index) => {
              const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
              return (
                <NaatCard
                  key={audio.id}
                  naat={audio}
                  serialNumber={(currentPage - 1) * pageSize + index + 1}
                  isPlaying={isPlaying}
                  isFavorite={isFavorite(audio.id)}
                  onPlay={handlePlayAudio}
                  onPause={() => togglePlay()}
                  onAddToQueue={handleAddToQueue}
                  onToggleFavorite={handleToggleFavorite}
                  onDownloadComplete={handleDownloadComplete}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Simple Pagination and Controls */}
      <div className="bottom-controls">
        <div className="simple-pagination">
          {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`page-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
        </div>
        <div className="page-size-control" style={{ display: 'none' }}>
          {/* Hidden - using fixed page size for SEO-friendly URLs */}
          <label>Items per page:</label>
          <select value={pageSize} disabled>
            <option value={15}>15</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default PopularNaatsPage;